/**
 * Le contrat du support et des litiges, côté navigateur (`EX-SUP-001`,
 * `EX-SUP-003`, `EX-BO-004`).
 *
 * **Ce fichier ne décide de rien.** Il transporte, et il déclare ce que le
 * serveur rend — les types suivent les `record` de `SupportApi`, sans rien y
 * ajouter. Un contrôle confronte champ par champ cette déclaration au fichier
 * Java lui-même : un champ renommé d'un seul côté produit un écran vide **sans
 * erreur**.
 *
 * ## Ce que ce chantier referme
 *
 * `SupportApi` servait **deux routes, éprouvées, et appelées par aucun écran**,
 * pendant que le socle exécutait déjà **six** actions d'écriture pour de vrai.
 * C'est la forme la plus coûteuse de la famille `D-25` / `D-30` / `D-35` : tout
 * était là, et personne ne pouvait s'en servir.
 *
 * ## Les six écritures passent par la route UNIQUE des actions
 *
 * `POST /api/backoffice/actions/{action}` — c'est le choix d'`EX-BO-002` : le
 * droit porte sur l'**action**, jamais sur l'écran. Une route par geste
 * inviterait à contrôler par famille, et le premier geste rangé dans la
 * mauvaise famille hériterait des droits de la mauvaise.
 *
 * Conséquence pour ce fichier : il n'y a **rien à écrire** pour les décisions,
 * sinon la charge utile que chaque exécuteur attend. Ces charges sont déclarées
 * ici parce qu'elles sont un contrat, pas un détail d'appel — se tromper de nom
 * de champ rend un refus que rien à l'écran ne distingue d'un refus de droit.
 *
 * ## Le motif de consultation est une constante
 *
 * Comme la file de double validation, le rapprochement et le listage du
 * journal : un agent ouvre sa file toujours pour la même raison — la dépiler.
 * Ce qui distingue une consultation d'une autre est déjà journalisé par le
 * serveur, qui écrit le ticket ouvert.
 */

import type { ApiClient } from './api-client.js';

/** Le chemin de la file, tel que `SupportApi` la sert. */
export const supportTicketsPath = '/api/backoffice/support/tickets';

/** Le chemin de la route unique des actions habilitables. */
export const backOfficeActionsPath = '/api/backoffice/actions';

/** Le motif écrit au journal pour toute lecture de ce module. */
export const supportReason = 'revue-des-reclamations';

/**
 * Les quatre gestes de traitement, tels que `SupportDesk` les nomme.
 *
 * C'est une liste **fermée**, et le serveur la défend : un geste inconnu rend
 * `SUP_GESTURE_UNKNOWN` en énumérant ceux qui existent. La recopier ici est un
 * miroir, avec ce qu'un miroir coûte — un cinquième geste ajouté côté serveur
 * rendrait cette table fausse en silence. C'est pourquoi un contrôle **relit
 * les constantes `GESTURE_*` de `SupportDesk.java`** et les confronte à cette
 * liste, dans l'ordre : c'est la conduite tenue pour les types d'entité du
 * journal d'audit (`D-35`, `D-45`).
 */
export const supportGestures = ['ASSIGN', 'REPLY', 'STATUS', 'CLOSE'] as const;

/** Un geste de traitement. */
export type SupportGesture = (typeof supportGestures)[number];

/** Les codes d'action de la matrice que cet écran emploie. */
export const supportActions = {
  /** Ouvrir une réclamation. */
  create: 'TICKET_CREATE',
  /** Affecter, répondre, changer d'état, clore. */
  handle: 'SUPPORT_TICKET_HANDLE',
} as const;

/**
 * Un ticket, tel que la file le rend. Suit `SupportApi.TicketView`.
 *
 * `transactionId`, `assignedTo` et `escalatedAt` peuvent manquer, et chacun
 * pour une raison qui se lit : toutes les réclamations ne portent pas sur une
 * opération, une file non dépilée n'a affecté personne, et une échéance encore
 * tenue n'a escaladé nulle part.
 *
 * `slaDueAt` est une **échéance**, pas une durée : c'est le serveur qui écrit
 * l'instant, à partir du `sla_minutes` de la catégorie. L'écran compare cet
 * instant à maintenant pour dire « dépassé » — il ne recalcule aucune échéance,
 * parce qu'une échéance recalculée par le navigateur sur son horloge à lui
 * serait fausse dès que les deux divergent.
 */
export interface SupportTicket {
  readonly id: string;
  readonly reference: string;
  readonly customerId: string;
  readonly categoryCode: string;
  readonly subject: string;
  readonly status: string;
  readonly priority: number;
  readonly channel: string;
  readonly transactionId: string | null;
  readonly assignedTo: string | null;
  readonly slaDueAt: string | null;
  readonly escalatedAt: string | null;
  readonly escalationLevel: number;
  readonly openedAt: string;
}

/**
 * La fiche client vue du support. Suit `SupportApi.CustomerView`.
 *
 * Elle peut manquer **entièrement** : un ticket dont le client a été clôturé
 * n'en a plus. L'écran le dit plutôt que de rendre une fiche vide, qui se
 * lirait comme un client sans solde.
 */
export interface SupportCustomer {
  readonly id: string;
  readonly publicRef: string;
  readonly status: string;
  readonly balanceMinorUnits: number | null;
  readonly currency: string | null;
}

/**
 * Un message du fil. Suit `SupportApi.MessageView`.
 *
 * `internal` sépare ce que le client lit de ce que l'agent écrit pour ses
 * collègues. **Les confondre à l'écran serait le pire défaut de ce module** :
 * un agent croirait avoir répondu au client alors qu'il a pris une note, ou —
 * pire — croirait annoter alors qu'il écrit au client.
 */
export interface SupportMessage {
  readonly id: number;
  readonly authorType: string;
  readonly body: string;
  readonly internal: boolean;
  readonly postedAt: string;
}

/** Un événement de l'historique. Suit `SupportApi.EventView`. */
export interface SupportEvent {
  readonly id: number;
  readonly eventType: string;
  readonly fromStatus: string | null;
  readonly toStatus: string | null;
  readonly actorType: string;
  readonly detail: string | null;
  readonly occurredAt: string;
}

/** Une opération liée. Suit `SupportApi.OperationView`. */
export interface SupportOperation {
  readonly publicRef: string;
  readonly txType: string;
  readonly status: string;
  readonly amount: number;
  readonly feeAmount: number;
  readonly initiatedAt: string;
}

/**
 * Le dossier complet d'un ticket. Suit `SupportApi.TicketFileView`.
 *
 * **Une seule réponse porte les cinq choses** — le ticket, le client, le fil,
 * l'historique et les opérations. C'est délibéré côté serveur, et c'est ce qui
 * permet à l'écran d'ouvrir un dossier avec **un** appel : cinq appels
 * écriraient cinq consultations au journal d'audit pour une seule ouverture.
 */
export interface SupportTicketFile {
  readonly ticket: SupportTicket;
  readonly customer: SupportCustomer | null;
  readonly thread: readonly SupportMessage[];
  readonly history: readonly SupportEvent[];
  readonly transactions: readonly SupportOperation[];
}

/** Ce que rend la route des actions. */
export interface ActionOutcome {
  readonly outcome: string;
  readonly approvalRequestId: string | null;
  readonly detail: string | null;
}

/**
 * Lit la file des réclamations.
 *
 * Aucune capture d'erreur : rendre une liste vide ferait confondre « aucune
 * réclamation n'attend » — un fait — avec « le serveur a refusé », qui n'en est
 * pas un. Sur une file de support, la confusion se lit comme « rien à faire »,
 * c'est-à-dire l'inverse de ce qui se passe.
 */
export async function fetchTicketQueue(
  client: ApiClient,
  reason: string = supportReason,
): Promise<readonly SupportTicket[]> {
  const parametres = new URLSearchParams({ reason });
  return client.request<readonly SupportTicket[]>(
    `${supportTicketsPath}?${parametres.toString()}`,
  );
}

/** Ouvre le dossier d'un ticket — fiche, fil, historique, opérations. */
export async function fetchTicketFile(
  client: ApiClient,
  id: string,
  reason: string = supportReason,
): Promise<SupportTicketFile> {
  const parametres = new URLSearchParams({ reason });
  const dossier = await client.request<SupportTicketFile>(
    `${supportTicketsPath}/${encodeURIComponent(id)}?${parametres.toString()}`,
  );
  return exigerUnDossier(dossier);
}

/**
 * Demande un geste de traitement sur un ticket.
 *
 * Le motif est **saisi**, décision par décision, et non constant : un agent
 * affecte, répond ou clôt pour une raison qui lui est propre, et c'est elle que
 * le journal doit porter. Confondre ce motif avec celui de la consultation
 * inscrirait « revue des réclamations » comme motif d'une clôture.
 */
export async function handleTicket(
  client: ApiClient,
  demande: {
    readonly ticketId: string;
    readonly gesture: SupportGesture;
    readonly reason: string;
    readonly payload?: Readonly<Record<string, string>>;
  },
): Promise<ActionOutcome> {
  return client.request<ActionOutcome>(
    `${backOfficeActionsPath}/${supportActions.handle}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        entityRef: demande.ticketId,
        reason: demande.reason,
        payload: {
          ticketId: demande.ticketId,
          gesture: demande.gesture,
          ...demande.payload,
        },
      }),
    },
  );
}

/** Ouvre une réclamation. */
export async function createTicket(
  client: ApiClient,
  demande: {
    readonly customerId: string;
    readonly categoryCode: string;
    readonly subject: string;
    readonly description: string;
    readonly reason: string;
  },
): Promise<ActionOutcome> {
  return client.request<ActionOutcome>(
    `${backOfficeActionsPath}/${supportActions.create}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        entityRef: demande.customerId,
        reason: demande.reason,
        payload: {
          customerId: demande.customerId,
          categoryCode: demande.categoryCode,
          subject: demande.subject,
          description: demande.description,
        },
      }),
    },
  );
}

/**
 * Refuse ce qui n'est **pas** un dossier de réclamation.
 *
 * Le contrat servi est éprouvé champ par champ contre les `record` Java, mais
 * cela vaut pour ce que le serveur *déclare* ; à l'exécution, une réponse d'une
 * autre forme arriverait ici sans rien dire, et l'écran s'écroulerait sur une
 * lecture de `thread.length`. Un agent verrait alors un écran blanc au milieu
 * du traitement d'une réclamation.
 */
function exigerUnDossier(dossier: SupportTicketFile): SupportTicketFile {
  if (dossier?.ticket?.id === undefined || !Array.isArray(dossier?.thread)
    || !Array.isArray(dossier?.history)) {
    throw new Error(
      'ÉCHEC : la réponse n’est pas un dossier de réclamation — le ticket, le ' +
        'fil et l’historique sont ce qui le définit.',
    );
  }
  return dossier;
}
